import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import accountStore from '@/stores/account'
import { registerStores } from '@/helpers/stores'
import { KEY_TAB, KEY_ARROW_DOWN } from '@/helpers/keyCodes'
import { toggleDocumentOverflow } from '@/helpers/scroll'
import { equalizeLineLengthOnBreak } from '@/helpers/string'
import ImageContentful from '@/components/_shared/ImageContentful.vue'
import Search from '@/components/Search/Search.vue'
import NavLogo from '@/components/Navigation/NavLogo/NavLogo.vue'
import AdditionalMainLinks from '@/components/Navigation/AdditionalMainLinks/AdditionalMainLinks.vue'
import AccountNav from '@/components/Navigation/AccountNav/AccountNav.vue'
import MiniCart from '@/components/Navigation/MiniCart/MiniCart.vue'

const Debouncer = On.Debouncer()

let latestLink = ''

export default {
  name: 'DesktopNav',
  components: {
    ImageContentful,
    Search,
    NavLogo,
    AdditionalMainLinks,
    AccountNav,
    MiniCart,
  },

  data() {
    return {
      scrollPosition: 0,
      subNavIsTransitioning: false,
      wideDesktopViewport: window.innerWidth > 1400,
    }
  },

  computed: {
    ...mapState('nav', ['navigationVariant']),
    ...mapGetters({
      additionalMainLinks: 'nav/additionalMainLinks',
      currentTopic: 'nav/currentTopic',
      currentAdditionalNav: 'nav/currentAdditionalNav',
      deliveryNotice: 'nav/deliveryNotice',
      dropdownNavIsBlocked: 'nav/dropdownNavIsBlocked',
      shouldAvoidBlockToggling: 'nav/shouldAvoidBlockToggling',
      homepageUrl: 'page/homepageUrl',
      orderIsInitialized: 'order/dataIsInitialized',
      overlayIsOpen: 'nav/overlayIsOpen',
      subNavIsOpen: 'nav/subNavIsOpen',
      scrollbarWidth: 'page/scrollbarWidth',
      scrollbarEqualizerMargin: 'page/scrollbarEqualizerMargin',
      topics: 'nav/topics',
      vpWidth: 'viewport/width',
      hasFetchedCurrentUser: 'account/hasFetchedCurrentUser',
    }),

    subNavBlocks() {
      return this.topic.blocks
    },

    topic() {
      const emptyTopic = { teaser: {}, blocks: [] }
      return this.topics[this.currentTopic] || emptyTopic
    },

    deliveryNoticeStyle() {
      return `max-width: ${300 * this.subNavBlocks.length}px;`
    },

    formattedDeliveryNotice() {
      return this.deliveryNotice
        ? equalizeLineLengthOnBreak(this.deliveryNotice, { firstLineRatio: 0.6 })
        : ''
    },

    shouldRenderAccountNav() {
      const controller = document.body.getAttribute('data-controller')

      return (
        this.hasFetchedCurrentUser &&
        controller != 'user_sessions' &&
        controller != 'user_registrations' &&
        controller != 'user_passwords'
      )
    },

    isTeaserShowing() {
      return this.topic.teaser && this.wideDesktopViewport
    },

    overlayIsShown() {
      return (
        (this.overlayIsOpen ||
          this.currentAdditionalNav === 'mini-cart' ||
          this.currentAdditionalNav === 'search') &&
        this.orderIsInitialized
      )
    },
  },

  watch: {
    currentAdditionalNav() {
      if (this.currentAdditionalNav !== 'mini-cart' && this.currentAdditionalNav !== 'search') {
        this.setOverlayState(false)
      }
    },

    overlayIsOpen: 'setScrollbarEqualizers',
    vpWidth: 'handleResize',
  },

  beforeCreate() {
    registerStores({ account: accountStore })
  },

  mounted() {
    this.$store.dispatch('account/fetchData')
    // In case a user hovers over the navigation on initial load,
    // the dropdownNav (subnav and additional nav) should not open.
    // To prevent opening, dropdownNav is blocked (from opening) by default.
    // We don't unblock dropdownNav as long as the cursor is inside a main link.
    this.addDropdownNavBlockToggleCondition(e =>
      e.target.parentNode.contains(document.querySelector('.main-links__link'))
    )
    this.addDropdownNavBlockToggleCondition(e =>
      e.target.parentNode.contains(document.querySelector('.additional-main-links__link'))
    )
    document.body.addEventListener('mouseover', this.conditinallyUnblockDropdownNav)
    window.addEventListener('beforeUnload', function(event) {
      event.preventDefault()
    })
  },

  methods: {
    ...mapActions({
      setAdditionalNav: 'nav/setAdditionalNav',
      toggleSubnav: 'nav/toggleSubnav',
      reportEngagementToDy: 'dynamicYield/reportEngagementToDy',
    }),

    ...mapMutations({
      blockDropdownNav: 'nav/blockDropdownNav',
      setCurrentBlock: 'nav/setCurrentBlock',
      setCurrentTopic: 'nav/setCurrentTopic',
      setOverlayState: 'nav/setOverlayState',
      unblockDropdownNav: 'nav/unblockDropdownNav',
      addDropdownNavBlockToggleCondition: 'nav/addDropdownNavBlockToggleCondition',
    }),

    conditinallyUnblockDropdownNav(e) {
      if (!this.shouldAvoidBlockToggling(e)) {
        this.unblockDropdownNav()
        document.body.removeEventListener('mouseover', this.conditinallyUnblockDropdownNav)
      }
    },

    handleResize() {
      this.setAdditionalNav('')
      let self = this
      Debouncer.debounce({
        debounceTime: 100,
        callbackArgument: self,
        callback: function() {
          return (self.wideDesktopViewport = window.innerWidth > 1400)
        },
      })
    },

    setScrollbarEqualizers() {
      toggleDocumentOverflow(!this.overlayIsOpen)

      const helpButton = document.querySelector('.helpButton')
      if (helpButton) {
        helpButton.style.right = this.overlayIsOpen ? `${this.scrollbarWidth + 12}px` : ''
      }
    },

    onMainLinkMouseenter(link, index) {
      link.blocks.length ? this.openSubnav(index) : this.toggleSubnav(false)
      this.setCurrentBlock(0)
      this.setAdditionalNav('')

      // Only count as open if last link hovered wasn't the same and this
      // ...topic has containing blocks => subnav will not open otherwise
      if (latestLink !== link.href && link.blocks.length)
        On.Analytics.trackNavigation.open(link.href)
      latestLink = link.href
    },

    handleNavClick($event) {
      const et = $event.target
      const pn = et.parentNode

      // If either the clicked tag or its parent is an anchor, then return their href
      const href = et.tagName === 'A' ? et.href : pn.tagName === 'A' ? pn.href : null
      if (href) {
        On.Analytics.trackNavigation.click(href)
        if (href.includes('shoe-finder'))
          this.reportEngagementToDy(this.navigationVariant.dyCampaign)
      }
    },

    onMainLinkLeave() {
      this.closeSubnav()
      this.unblockDropdownNav()
    },

    onMainLinkClick() {
      this.closeSubnav()
      this.blockDropdownNav()
    },

    onMainLinkKeydown($event, index) {
      this.jumpToSubnav($event)
      this.openSubnav(index)
    },

    toggleSkipNavigation(closeSubnav) {
      const self = this
      this.$nextTick(function() {
        self.$refs.skipNavigation.classList.toggle('has-focus')
      })
      if (closeSubnav) this.closeSubnav()
    },

    closeSubnav(el) {
      if (el !== undefined && !el.currentTarget.hasAttribute('href')) return
      this.toggleSubnav(false)
    },

    openSubnav(currentTopic) {
      // If we change the topic, a transition on the subnav content is triggered
      // ...which is gonna be canceled on transitionend
      if (this.currentTopic !== currentTopic) this.subNavIsTransitioning = true
      if (currentTopic >= 0) this.setCurrentTopic(currentTopic)
      if (!this.dropdownNavIsBlocked) this.toggleSubnav(true)
    },

    resetTransitionStatus() {
      this.subNavIsTransitioning = false
    },

    jumpToSubnav($event) {
      if ($event.keyCode === KEY_ARROW_DOWN) {
        // With arrow down key, jump to first link in subnav
        this.$refs.blocktitle[0].focus()
        this.subNavHasFocus = true
        this.registerSubnavKeyListener()
      }
    },

    findNextTabStop(el) {
      const potentialTabStops = [
        ...document.querySelectorAll('input, button, select, textarea, a[href]'),
      ]
      const tabStops = potentialTabStops.filter(stop => stop.tabIndex >= '0')
      const index = tabStops.indexOf(el)
      return tabStops[index + 1] || tabStops[0]
    },

    registerSubnavKeyListener() {
      window.addEventListener('keydown', this.checkSubNavLeave)
    },

    removeSubnavKeyListener() {
      window.removeEventListener('keydown', this.checkSubNavLeave)
    },

    // Accessibilty handling
    checkSubNavLeave(event) {
      // Checks on tab if the current stop was the last possible tabstop inside subnav
      // If true finds the element which shall be focussable next outside subnav
      if (event.keyCode === KEY_TAB) {
        const nextTabStop = this.findNextTabStop(document.activeElement)
        const nextTabStopIsInsideHeader = nextTabStop.closest('header') !== null
        if (!nextTabStopIsInsideHeader) {
          event.preventDefault()
          const nextFocussableLink =
            document.activeElement.closest('.sub-navigation__main-content') !== null
              ? this.$refs.mainLink[this.currentTopic + 1]
              : document.querySelector('.trigger-' + this.$state.store.currentAdditionalNav)
                .nextSibling
          nextFocussableLink !== null
            ? nextFocussableLink.focus()
            : document.querySelector('.site-content').focus()
          this.removeSubnavKeyListener()
        }
      }
    },
  },
}
